import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./styles.css";
import {
  Form,
  Input,
  Row,
  Col,
  Select,
  Spin,
  Button,
  Result,
} from "antd";
import { db, storage } from "../../../services/firebase";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import Container from "../../../components/Container";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Item, useForm } = Form;

function CertificateForm() {
  const { id } = useParams();
  const [form] = useForm();
  const [clientes, setClientes] = useState([]);
  const [valvulas, setValvulas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [valvulasOptions, setValvulasOptions] = useState([]);
  const [filesCertificados, setFilesCertificados] = useState([]);
  const [filesDatabook, setFilesDatabook] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const navigate = useNavigate();

  // Busca clientes
  const fetchClientesFirebase = async () => {
    try {
      const snapshot = await getDocs(collection(db, "clientes"));
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClientes(data);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    }
  };

  // Busca válvulas
  const fetchFromCollection = async (collectionName) => {
    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  const fetchValvulasFirebase = async () => {
    try {
      const [valvulasBloqueio, valvulasPSV, valvulasControle] =
        await Promise.all([
          fetchFromCollection("valvulasBloqueio"),
          fetchFromCollection("valvulasPSV"),
          fetchFromCollection("valvulasControle"),
        ]);

      const data = [...valvulasBloqueio, ...valvulasControle, ...valvulasPSV];
      setValvulas(data);
    } catch (error) {
      console.error("Erro ao listar válvulas:", error);
    }
  };

  // Carrega arquivos de certificado/databook no Storage
  async function getFiles() {
    if (!id) return;

    try {
      const listCertificadosRef = ref(storage, `${id}/certificados`);
      const listDatabookRef = ref(storage, `${id}/databook`);

      const [resultadoCertificados, resultadoDatabook] = await Promise.all([
        listAll(listCertificadosRef),
        listAll(listDatabookRef),
      ]);

      const pdfCertificados = await Promise.all(
        resultadoCertificados.items.map((pdf) => getDownloadURL(pdf))
      );

      const pdfDatabook = await Promise.all(
        resultadoDatabook.items.map((pdf) => getDownloadURL(pdf))
      );

      setFilesCertificados(pdfCertificados);
      setFilesDatabook(pdfDatabook);
    } catch (error) {
      console.error("Erro ao buscar PDFs:", error);
    }
  }

  // Carrega o certificado do Firestore
  const fetchCertificate = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const refCert = doc(db, "certificados", id);
      const snapshot = await getDoc(refCert);

      if (!snapshot.exists()) {
        setAccessDenied(true);
        return;
      }

      const data = { id: snapshot.id, ...snapshot.data() };

      // Verificar se o cliente logado tem acesso a este certificado
      const seepilUsuario = JSON.parse(sessionStorage.getItem("seepilUsuario") || "{}");
      const clienteIdLogado = seepilUsuario?.cliente_id;

      // Buscar unidades permitidas (compatível com array de strings ou string única)
      const unidadesPermitidas =
        Array.isArray(seepilUsuario?.unidadesCliente) && seepilUsuario.unidadesCliente.length > 0
          ? seepilUsuario.unidadesCliente
          : seepilUsuario?.unidadeCliente
          ? [seepilUsuario.unidadeCliente]
          : [];

      // Verificar se o certificado pertence ao cliente logado
      if (data.cliente_id !== clienteIdLogado) {
        setAccessDenied(true);
        return;
      }

      // Verificar se a unidade do certificado está nas unidades permitidas do usuário
      const temAcessoUnidade = unidadesPermitidas.includes(data.unidadeCliente);

      if (!temAcessoUnidade) {
        setAccessDenied(true);
        return;
      }

      form.setFieldsValue(data);

      // monta unidades do cliente
      const clienteDoCert = data.cliente_id;
      const cliente = clientes.find((c) => c.id === clienteDoCert);
      setUnidades(cliente?.unidades || []);

      // monta válvulas da unidade
      const valvulasDaUnidade = valvulas.filter(
        (valvula) =>
          valvula.cliente_id === data.cliente_id &&
          valvula.unidadeCliente === data.unidadeCliente
      );
      setValvulasOptions(valvulasDaUnidade);
    } catch (error) {
      console.error("Erro ao buscar certificado:", error);
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  // Carrega clientes/válvulas uma vez
  useEffect(() => {
    fetchClientesFirebase();
    fetchValvulasFirebase();
  }, []);

  // Quando clientes e válvulas estiverem carregados, busca o certificado
  useEffect(() => {
    if (clientes.length > 0 && valvulas.length > 0 && id) {
      fetchCertificate();
      getFiles();
    }
  }, [clientes, valvulas, id]);

  // Tela de acesso negado
  if (accessDenied) {
    return (
      <Container userType="cliente">
        <div className="certificate-form-content">
          <div className="certificate-form-container">
            <Result
              status="403"
              title="Acesso Negado"
              subTitle="Você não tem permissão para visualizar este certificado. Este certificado pertence a outro cliente ou unidade."
              extra={
                <Button type="primary" onClick={() => navigate("/cliente/certificados")}>
                  Voltar para Meus Certificados
                </Button>
              }
            />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container userType="cliente">
      <Spin spinning={loading} tip="Processando..." size="large">
        <div style={{ minHeight: "400px" }}>
          <div className="certificate-form-content">
            <div className="certificate-form-container">
              <Form
                layout="vertical"
                form={form}
                disabled // 🔒 tudo somente leitura para o cliente
              >
                <h2>Certificado</h2>

                <Item label="Cliente" name="cliente_id">
                  <Select
                    allowClear
                    placeholder="Selecione um cliente"
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      minWidth: "250px",
                    }}
                  >
                    {clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nomeEmpresa}
                      </option>
                    ))}
                  </Select>
                </Item>

                <Item label="Unidade" name="unidadeCliente">
                  <Select
                    allowClear
                    placeholder="Selecione uma unidade"
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      minWidth: "250px",
                    }}
                  >
                    {unidades.map((unidade, index) => (
                      <option key={index} value={unidade.unidade}>
                        {unidade.unidade}
                      </option>
                    ))}
                  </Select>
                </Item>

                <Item label="Válvula" name="valvula_id">
                  <Select
                    allowClear
                    placeholder="Selecione uma válvula"
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      minWidth: "250px",
                    }}
                  >
                    {valvulasOptions.map((valvula) => (
                      <option key={valvula.id} value={valvula.id}>
                        Tipo: {valvula.tipoValvula} / Tag: {valvula.tag} / Nº
                        Série: {valvula.numeroSerie}
                      </option>
                    ))}
                  </Select>
                </Item>

                <Row gutter={[12]}>
                  <Col span={12}>
                    <Item label="Tipo de Certificado:" name="tipoCertificado">
                      <Select>
                        <option value="onshore">ONSHORE</option>
                        <option value="offshore">OFFSHORE</option>
                        <option value="fornecimento">FORNECIMENTO</option>
                      </Select>
                    </Item>
                  </Col>
                  <Col span={12}>
                    <Item label="Nº Certificado" name="numeroCertificado">
                      <Input placeholder="Nº Certificado" />
                    </Item>
                  </Col>
                </Row>

                <Item label="Controle Interno SPL" name="spl">
                  <Input placeholder="Controle Interno SPL" />
                </Item>

                <Item label="Tag" name="tag">
                  <Input placeholder="Tag" />
                </Item>

                <Item label="Fabricante:" name="fabricante">
                  <Input placeholder="" />
                </Item>

                <Item label="Modelo:" name="modelo">
                  <Input placeholder="" />
                </Item>

                <Item label="Número de Série:" name="numeroSerie">
                  <Input placeholder="" />
                </Item>

                <Item label="Tipo da Válvula:" name="tipoValvula">
                  <Input placeholder="Tipo da Válvula" />
                </Item>

                {/* PDFs – apenas visualização */}
   <Row gutter={24} className="pdf-links-row">
  <Col span={12}>
    <h3 className="titulo-upload">Certificados:</h3>
    <div className="pdf-links-list">
      {filesCertificados.map((item, index) => (
        <a
          key={index}
          href={item}
          target="_blank"
          rel="noreferrer"
          className="pdf-link"
        >
          Visualizar PDF Certificado
          {filesCertificados.length > 1 ? ` (${index + 1})` : ""}
        </a>
      ))}
      {filesCertificados.length === 0 && (
        <span className="pdf-empty-text">Nenhum certificado disponível.</span>
      )}
    </div>
  </Col>

  <Col span={12}>
    <h3 className="titulo-upload">Databook:</h3>
    <div className="pdf-links-list">
      {filesDatabook.map((item, index) => (
        <a
          key={index}
          href={item}
          target="_blank"
          rel="noreferrer"
          className="pdf-link"
        >
          Visualizar PDF Databook
          {filesDatabook.length > 1 ? ` (${index + 1})` : ""}
        </a>
      ))}
      {filesDatabook.length === 0 && (
        <span className="pdf-empty-text">Nenhum databook disponível.</span>
      )}
    </div>
  </Col>
</Row>


                {/* ⚠️ nenhum botão de salvar/editar aqui */}
              </Form>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 24,
                }}
              >
                <Button
                  className="voltarBotao"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                >
                  Voltar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Spin>
    </Container>
  );
}

export default CertificateForm;
